/**
 * EIN ABSENDEN-KNOPF JE GEOEFFNETER BUEHNE (Rafael, 25.10.2026: „das macht ja nur Sinn, dass da nur einer ist, der alles schickt“) — Phase 2 der Guide-Flaeche.
 *
 * Rein (kein DOM, kein Speicher): was der Knopf sagt und tut, aus zwei Dingen — den TEILEN, die gerade im Feld stehen (gewaehlte Antworten, freier Text), und den Auftraegen
 * dieser Buehne in der durablen Sendewarteschlange (GuideSendQueue). Jeder Teil ist ein EIGENER Auftrag mit eigener Kennung, der Server quittiert je Kennung — nichts wird
 * doppelt gesendet: ein Teil, zu dem schon ein unquittierter Auftrag existiert, wird NIE noch einmal eingereiht, sondern erscheint als „unterwegs“ oder „nicht angekommen“.
 * Die Entwuerfe (Wahl, Notiz, freier Text) leert die Warteschlange erst bei der Quittung des jeweiligen Auftrags; bei einem Teilfehler bleibt also der Text im Feld.
 * Auch die Vorschlags-Antworten sind ein eigener Teil mit eigener Kennung: der Server nimmt `/vorschlaege/antworten` idempotent an (`x-guide-job-id`, wie Antworten und Nachricht).
 */
/**
 * Was nach der Quittung aus dem Entwurf verschwindet: der GESENDETE Teil, nicht mehr. Verglichen wird getrimmt (der Auftrag traegt `text.trim()`, der Entwurf den rohen Text) —
 * sonst bliebe Text mit Randleerzeichen stehen und ein zweiter Klick sendete ihn doppelt. Hat Rafael nach dem Absenden weitergetippt, bleibt nur der neue Rest („alter Text“
 * faellt weg, „und mehr“ bleibt). `null` = unveraendert lassen (der Entwurf wurde mitten im Text geaendert: dann lieber stehen lassen als falsch kuerzen); `''` = ganz entfernen.
 */
export function zieheGesendetAb(gesendet: string, aktuell: string): string | null {
  const g = gesendet.trim();
  const a = aktuell.trim();
  if (!g) return null;
  if (a === g) return '';
  if (a.startsWith(g)) return a.slice(g.length).replace(/^\s+/, '');
  return null;
}

export type AbsendeArt = 'antwort' | 'vorschlag' | 'nachricht';
export interface AbsendeTeil { art: AbsendeArt; anzahl: number; /** Eine Zeile fuer die Zusammenfassung vor dem Senden. */ beschreibung?: string }
export interface AbsendeAuftrag { id: string; art: AbsendeArt; state: string; rejected?: boolean; serverAccepted?: boolean; error?: string }

/** Ein Auftrag der Sendewarteschlange ist eine Nachricht, wenn er `text` traegt, eine Vorschlags-Antwort mit `vorschlaege`, sonst eine Antwort (`choices`). */
export const auftragsArt = (j: { text?: string; vorschlaege?: unknown[] }): AbsendeArt => (j.text !== undefined ? 'nachricht' : j.vorschlaege ? 'vorschlag' : 'antwort');
/** „nicht angekommen“: der Server hat den Auftrag abgewiesen oder er ist gesperrt — nur dafuer gibt es „Erneut senden“. Alles andere ist „unterwegs“. */
export const istFehler = (j: AbsendeAuftrag): boolean => j.state === 'blocked' || (j.rejected === true && j.serverAccepted !== true);

const teilWort = (t: AbsendeTeil): string => t.art === 'antwort' ? (t.anzahl === 1 ? '1 Antwort' : `${t.anzahl} Antworten`)
  : t.art === 'vorschlag' ? (t.anzahl === 1 ? '1 Vorschlags-Antwort' : `${t.anzahl} Vorschlags-Antworten`) : (t.anzahl === 1 ? 'Nachricht' : `${t.anzahl} Nachrichten`);
export const artWort = (a: AbsendeArt) => (a === 'antwort' ? 'Antwort' : a === 'vorschlag' ? 'Vorschlags-Antwort' : 'Nachricht');

/** Fasst gleiche Arten zusammen (zwei Fragen = eine Zeile „2 Antworten“); Antworten stehen immer vor den Vorschlags-Antworten, die Nachricht zuletzt. */
export function fasseZusammen(teile: AbsendeTeil[]): AbsendeTeil[] {
  const aus: AbsendeTeil[] = [];
  for (const art of ['antwort', 'vorschlag', 'nachricht'] as const) {
    const t = teile.filter(x => x.art === art && x.anzahl > 0);
    if (t.length) aus.push({ art, anzahl: t.reduce((n, x) => n + x.anzahl, 0), beschreibung: t.map(x => x.beschreibung).filter(Boolean).join(' · ') || undefined });
  }
  return aus;
}

export interface AbsendeZustand {
  art: 'leer' | 'bereit' | 'sendet' | 'teilfehler';
  /** Was noch eingereiht werden muss (Teile OHNE laufenden Auftrag). */
  offen: AbsendeTeil[];
  unterwegs: AbsendeAuftrag[];
  fehler: AbsendeAuftrag[];
  label: string;
  satz: string;
  zusammenfassung: string[];
  /** Darf der Knopf klicken? (bereit oder Teilfehler: erneut senden) */
  klickbar: boolean;
}
export function absendeZustand(teile: AbsendeTeil[], auftraege: AbsendeAuftrag[]): AbsendeZustand {
  const offen = fasseZusammen(teile).filter(t => !auftraege.some(j => j.art === t.art));
  const fehler = auftraege.filter(istFehler);
  const unterwegs = auftraege.filter(j => !istFehler(j));
  const zusammenfassung = offen.map(t => t.beschreibung ?? teilWort(t));
  if (fehler.length) {
    const arten = [...new Set(fehler.map(j => artWort(j.art)))].join(' + ');
    return { art: 'teilfehler', offen, unterwegs, fehler, klickbar: true, zusammenfassung,
      label: `${arten} erneut senden`,
      satz: 'Ein Teil ist nicht angekommen. Dein Text bleibt stehen; erneut gesendet wird nur dieser Teil — was schon angekommen ist, geht nicht noch einmal.' };
  }
  if (offen.length) {
    return { art: 'bereit', offen, unterwegs, fehler, klickbar: true, zusammenfassung,
      label: `Absenden an den Berater: ${offen.map(teilWort).join(' + ')}`,
      satz: `Geht an den Berater dieser Bühne: ${offen.map(teilWort).join(' + ')}.` };
  }
  if (unterwegs.length) {
    return { art: 'sendet', offen, unterwegs, fehler, klickbar: false, zusammenfassung,
      label: 'wird gesendet …', satz: 'Wird gesendet: erst die Antworten, dann die Vorschlags-Antworten, zuletzt die Nachricht — jeder Teil mit eigener Kennung, nichts doppelt.' };
  }
  return { art: 'leer', offen, unterwegs, fehler, klickbar: false, zusammenfassung,
    label: 'Absenden an den Berater', satz: 'Nichts zu senden — wähle eine Antwort oder schreib dem Berater etwas.' };
}
